import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TEST_IDS } from "@/constants/testIds";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Trash2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PhotoPicker from "@/components/PhotoPicker";

const STATUSES = [
  "available","laundry","under_maintenance","temporarily_unavailable",
  "seasonal_storage","donated","sold","archived",
];

const STATUS_LABEL = {
  available: "Available",
  laundry: "Laundry",
  under_maintenance: "Maintenance",
  temporarily_unavailable: "Unavailable",
  seasonal_storage: "Storage",
  donated: "Donated",
  sold: "Sold",
  archived: "Archived",
};

export default function Wardrobe() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const { data } = await api.get("/wardrobe");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      if (files.length === 1) {
        const fd = new FormData();
        fd.append("image", files[0]);
        const { data } = await api.post("/wardrobe", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setItems((arr) => [data, ...arr]);
        toast.success(`Added ${data.type}`);
      } else {
        const fd = new FormData();
        files.forEach((f) => fd.append("images", f));
        const { data } = await api.post("/wardrobe/bulk", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setItems((arr) => [...data.added, ...arr]);
        toast.success(`Added ${data.added.length} pieces${data.errors.length ? ` · ${data.errors.length} failed` : ""}`);
      }
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const updateStatus = async (id, status) => {
    const { data } = await api.patch(`/wardrobe/${id}`, { status });
    setItems((arr) => arr.map((i) => i.id === id ? data : i));
  };

  const remove = async (id) => {
    await api.delete(`/wardrobe/${id}`);
    setItems((arr) => arr.filter((i) => i.id !== id));
  };

  const categories = ["all", "clothing", "footwear", "accessory"];
  const visible = filter === "all" ? items : items.filter((i) => i.category === filter);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow">Closet · {items.length} pieces</p>
          <h1 className="editorial-h1 mt-3">Wardrobe.</h1>
        </div>
        <div className="flex items-center gap-3">
          <PhotoPicker
            onSelect={upload}
            multiple
            testId="wardrobe-picker"
            disabled={uploading}
          >
            <Button
              data-testid={TEST_IDS.wardrobe.uploadBtn}
              disabled={uploading}
              className="rounded-none uppercase tracking-[0.2em] text-xs h-11"
            >
              {uploading ? <><Loader2 size={14} className="animate-spin mr-2" />Analyzing</> : <><Plus size={14} className="mr-2" /> Add piece</>}
            </Button>
          </PhotoPicker>
        </div>
      </div>

      <div className="mt-8 flex gap-2 flex-wrap">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            data-testid={`wardrobe-filter-${c}`}
            className={`px-4 py-2 border text-[11px] uppercase tracking-[0.2em] ${
              filter === c ? "bg-accent text-accent-foreground border-foreground" : "border-border hover:border-foreground"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {visible.map((it) => (
          <div
            key={it.id}
            data-testid={TEST_IDS.wardrobe.item(it.id)}
            className="border border-border bg-card group"
          >
            <div className="aspect-square overflow-hidden bg-muted relative">
              <img src={fileUrl(it.image_path)} alt={it.type} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <button
                onClick={() => remove(it.id)}
                data-testid={TEST_IDS.wardrobe.deleteBtn(it.id)}
                className="absolute top-2 right-2 p-1.5 bg-background/80 backdrop-blur opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div className="p-3 space-y-2">
              <div className="flex items-baseline justify-between">
                <p className="text-sm font-medium truncate">{it.type}</p>
                <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">F{it.formality}</span>
              </div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {it.color} · {it.pattern}
              </p>
              <Select value={it.status} onValueChange={(v) => updateStatus(it.id, v)}>
                <SelectTrigger
                  data-testid={TEST_IDS.wardrobe.statusSelect(it.id)}
                  className="rounded-none h-8 text-[10px] uppercase tracking-[0.2em]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}

        {visible.length === 0 && !uploading && (
          <div className="col-span-full border border-dashed border-border p-16 text-center">
            <p className="eyebrow">Empty closet</p>
            <p className="font-serif text-3xl mt-3">Upload your first piece.</p>
            <p className="text-sm text-muted-foreground mt-3 max-w-md mx-auto">
              Stylebay analyzes each garment — type, colour, fabric, formality — and learns your taste.
            </p>
            <PhotoPicker
              onSelect={upload}
              multiple
              testId="wardrobe-empty-picker"
              disabled={uploading}
            >
              <Button
                className="mt-6 rounded-none uppercase tracking-[0.2em] text-xs"
                data-testid="wardrobe-empty-upload-btn"
              >
                <Plus size={14} className="mr-2" /> Upload garment
              </Button>
            </PhotoPicker>
          </div>
        )}
      </div>
    </div>
  );
}
